/**
 * Budjet o'ng tugma menyulari: xarajat, maosh, bonus -- holatga qarab (test: menu.test.ts).
 * Shartlar sahifadagi tugmalar bilan bir xil; ruxsat backend'da ham tekshiriladi.
 */
import { Archive, BadgeCheck, Check, Clock3, Copy, Eye, ListTree, Paperclip, Pencil, RotateCcw, Truck, Wallet, X } from "lucide-react";
import { compactMenu, SEPARATOR, type MenuEntry } from "@/components/ui/context-menu";
import type { Translate } from "@/features/drivers/menu";
import type { Bonus, Expense, PlacementFee, SalaryRow } from "./types";
import { profileItem, type ProfileAction } from "@/features/people/profileAction";

export interface ExpenseMenuActions {
  edit: () => void;
  archive: () => void;
  navigate: (path: string) => void;
  openUrl: (url: string) => void;
  copy: (text: string) => void;
  profile?: ProfileAction;
  /** Tasdiqlangan, hali to'lanmagan xarajatni hisobdan to'lash */
  pay?: () => void;
}

export function expenseMenu(e: Expense, a: ExpenseMenuActions, t: Translate): MenuEntry[] {
  const pending = e.status === "pending";
  return compactMenu([
    pending && { key: "edit", label: t("edit"), icon: Pencil, onSelect: a.edit },
    pending && { key: "review", label: t("budget.reviewInApprovals"), icon: BadgeCheck, onSelect: () => a.navigate("/approvals?kind=expense") },
    e.status === "approved" && !e.payment && a.pay && { key: "pay", label: t("budget.payExpense"), icon: Wallet, onSelect: a.pay },
    !!e.receipt_url && { key: "receipt", label: t("budget.openReceipt"), icon: Paperclip, hint: e.receipt_name, onSelect: () => a.openUrl(e.receipt_url as string) },
    profileItem(a.profile, e.submitted_by?.id, t, { name: e.submitted_by?.display_name, key: "submitter-profile" }),
    SEPARATOR,
    { key: "copy-amount", label: t("budget.copyAmount"), icon: Copy, hint: `${e.amount} ${e.currency}`, onSelect: () => a.copy(String(e.amount)) },
    !!e.description && { key: "copy-description", label: t("budget.copyDescription"), icon: Copy, onSelect: () => a.copy(e.description) },
    SEPARATOR,
    pending && { key: "archive", label: t("archive"), icon: Archive, danger: true, onSelect: a.archive },
  ]);
}

export interface SalaryMenuActions {
  markPaid: () => void;
  markUnpaid: () => void;
  copy: (text: string) => void;
  profile?: ProfileAction;
}

/** owner -- "to'landi" belgisini qaytara oladigan yagona rol */
export function salaryMenu(r: SalaryRow, owner: boolean, a: SalaryMenuActions, t: Translate): MenuEntry[] {
  const saved = r.id !== null;
  return compactMenu([
    profileItem(a.profile, r.user?.id, t, { name: r.user?.display_name, role: r.role }),
    SEPARATOR,
    saved && r.editable && !r.paid && { key: "paid", label: t("budget.markPaid"), icon: Check, onSelect: a.markPaid },
    saved && r.editable && r.paid && owner && { key: "unpaid", label: t("budget.markUnpaid"), icon: RotateCcw, onSelect: a.markUnpaid },
    SEPARATOR,
    r.amount !== null && { key: "copy-amount", label: t("budget.copyAmount"), icon: Copy, hint: `${r.amount} ${r.currency}`, onSelect: () => a.copy(String(r.amount)) },
  ]);
}

export interface BonusMenuActions {
  toggleDetails: () => void;
  adjust: () => void;
  approve: () => void;
  markPaid: () => void;
  copy: (text: string) => void;
  profile?: ProfileAction;
}

export function bonusMenu(b: Bonus, manager: boolean, expanded: boolean, a: BonusMenuActions, t: Translate): MenuEntry[] {
  return compactMenu([
    profileItem(a.profile, b.user?.id, t, { name: b.user?.display_name }),
    { key: "details", label: expanded ? t("budget.hideDetails") : t("budget.showDetails"), icon: ListTree, onSelect: a.toggleDetails },
    SEPARATOR,
    manager && b.status === "draft" && { key: "adjust", label: t("budget.adjust"), icon: Pencil, onSelect: a.adjust },
    manager && b.status === "draft" && Number(b.total) > 0 && { key: "approve", label: t("budget.approveBonus"), icon: BadgeCheck, onSelect: a.approve },
    manager && b.status === "approved" && { key: "paid", label: t("budget.markPaid"), icon: Wallet, onSelect: a.markPaid },
    SEPARATOR,
    { key: "copy-amount", label: t("budget.copyAmount"), icon: Copy, hint: `${b.total} ${b.currency}`, onSelect: () => a.copy(String(b.total)) },
  ]);
}

export interface PlacementMenuActions {
  navigate: (path: string) => void;
  /** Oyna: pul tushdi (summa, hisob majburiy, sana) */
  addPayment: () => void;
  /** Oyna: shartlar -- summa, muddat, izoh */
  edit: () => void;
  /** Yozib yuborish: carrier to'lamaydi (confirm bilan); faqat pul tushmagan bo'lsa */
  writeOff: () => void;
  reopen: () => void;
  /** Tushumni bekor qilish (daftarda arxiv) */
  voidPayment: (paymentId: number) => void;
  copy: (text: string) => void;
  profile?: ProfileAction;
}

/** Carrier to'lovi qatori: driver va carrier'ga o'tish; budget ruxsati bilan -- pul qabul qilish (faqat tasdiqlangan hire),
 * shartlar, yozib yuborish/qaytarish, tushumlarni bekor qilish. "To'landi" qo'lda qo'yilmaydi -- tushumdan hisoblanadi. */
export function placementMenu(p: Pick<PlacementFee, "driver" | "carrier" | "owner" | "fee_status" | "approved" | "received">, canEdit: boolean, a: PlacementMenuActions, t: Translate): MenuEntry[] {
  const open = p.fee_status === "pending" || p.fee_status === "partial";
  const hasMoney = p.received.length > 0;
  return compactMenu([
    { key: "driver", label: t("openDriver", { name: p.driver.full_name }), icon: Eye, onSelect: () => a.navigate(`/drivers/${p.driver.id}`) },
    { key: "carrier", label: t("driver.openCarrier", { name: p.carrier.name }), icon: Truck, onSelect: () => a.navigate(`/carriers/${p.carrier.id}`) },
    profileItem(a.profile, p.owner?.id, t, { name: p.owner?.display_name, key: "owner-profile" }),
    SEPARATOR,
    canEdit && open && !p.approved && { key: "awaiting", label: t("placement.awaitingApproval"), icon: Clock3, disabled: true },
    canEdit && open && p.approved && { key: "add-payment", label: t("placement.addPayment"), icon: Wallet, onSelect: a.addPayment },
    canEdit && { key: "edit", label: t("placement.edit"), icon: Pencil, onSelect: a.edit },
    canEdit && !hasMoney && p.fee_status !== "not_paid" && { key: "write-off", label: t("placement.writeOff"), icon: X, onSelect: a.writeOff },
    canEdit && p.fee_status === "not_paid" && { key: "reopen", label: t("placement.reopen"), icon: RotateCcw, onSelect: a.reopen },
    canEdit &&
      hasMoney && {
        key: "payments",
        label: t("placement.payments"),
        icon: ListTree,
        items: p.received.map((r) => ({ key: `void-${r.id}`, label: t("placement.voidPayment"), hint: `${r.amount} ${r.currency} · ${r.account}`, danger: true, onSelect: () => a.voidPayment(r.id) })),
      },
    SEPARATOR,
    { key: "copy-name", label: t("copyName"), icon: Copy, onSelect: () => a.copy(p.driver.full_name) },
  ]);
}

